import React from 'react';
import { StatusBadge } from '@/components/platform/StatusBadge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { HealthBand } from '@/lib/api/accounts';

interface AccountHealthBadgeProps {
  band: HealthBand;
  /** Optional context fields used to render the "why" tooltip. */
  lastSeenAt?: string | null;
  totalUsers?: number;
  highIntentUsers?: number;
  totalSessions?: number;
  size?: 'sm' | 'md';
}

const BAND_LABEL: Record<HealthBand, string> = {
  hot: 'Hot',
  warm: 'Warm',
  neutral: 'Neutral',
};

const BAND_DOT: Record<HealthBand, string> = {
  hot: 'bg-green-500 dark:bg-green-400',
  warm: 'bg-yellow-500 dark:bg-yellow-400',
  neutral: 'bg-muted-foreground/40',
};

const BAND_TONE: Record<HealthBand, 'success' | 'warning' | 'neutral'> = {
  hot: 'success',
  warm: 'warning',
  neutral: 'neutral',
};

const SCORE_FACTORS: { label: string; weight: string }[] = [
  { label: 'Recency', weight: '35%' },
  { label: 'Buying intent', weight: '25%' },
  { label: 'Engagement', weight: '20%' },
  { label: 'User coverage', weight: '10%' },
  { label: 'Booked sessions', weight: '6%' },
  { label: 'Qualified users', weight: '4%' },
];

const BANDS: { band: HealthBand; range: string }[] = [
  { band: 'hot', range: '60+' },
  { band: 'warm', range: '30–59' },
  { band: 'neutral', range: '0–29' },
];

function daysSince(iso?: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
}

function buildReason(props: AccountHealthBadgeProps): string {
  const days = daysSince(props.lastSeenAt);
  if (props.lastSeenAt == null || (days != null && days > 30)) {
    if (days == null) return 'No recorded activity yet.';
    return `No activity for ${days} day${days === 1 ? '' : 's'}.`;
  }

  const parts: string[] = [];
  if (days != null) parts.push(`Active ${days} day${days === 1 ? '' : 's'} ago`);
  if (typeof props.totalUsers === 'number') parts.push(`${props.totalUsers} user${props.totalUsers === 1 ? '' : 's'}`);
  if (typeof props.highIntentUsers === 'number' && props.highIntentUsers > 0) {
    parts.push(`${props.highIntentUsers} high-intent`);
  }
  if (typeof props.totalSessions === 'number') parts.push(`${props.totalSessions} session${props.totalSessions === 1 ? '' : 's'}`);

  return parts.length > 0 ? parts.join(' · ') : BAND_LABEL[props.band];
}

const AccountHealthBadge: React.FC<AccountHealthBadgeProps> = (props) => {
  const reason = buildReason(props);
  const sizeClass = props.size === 'md' ? 'h-6 px-2 text-xs' : 'h-5 px-1.5 text-[11px]';

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex focus:outline-none">
            <StatusBadge tone={BAND_TONE[props.band]} className={sizeClass}>
              {BAND_LABEL[props.band]}
            </StatusBadge>
          </span>
        </TooltipTrigger>
        <TooltipContent className="w-64 p-2.5">
          <div className="text-[11px]">
            <span className="font-semibold">{BAND_LABEL[props.band]}</span>
            <span className="text-muted-foreground"> · {reason}</span>
          </div>

          <div className="my-1.5 h-px bg-border" />

          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Calculation
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-[11px]">
            {SCORE_FACTORS.map((f) => (
              <div key={f.label} className="contents">
                <span>{f.label}</span>
                <span className="tabular-nums text-muted-foreground">{f.weight}</span>
              </div>
            ))}
          </div>

          <div className="my-1.5 h-px bg-border" />

          <div className="space-y-0.5 text-[11px]">
            {BANDS.map((b) => {
              const isCurrent = b.band === props.band;
              return (
                <div
                  key={b.band}
                  className={`flex items-center justify-between rounded px-1.5 py-0.5 ${
                    isCurrent ? 'bg-accent font-semibold' : ''
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <span className={`h-1.5 w-1.5 rounded-full ${BAND_DOT[b.band]}`} />
                    {BAND_LABEL[b.band]}
                  </span>
                  <span className={`tabular-nums ${isCurrent ? '' : 'text-muted-foreground'}`}>
                    {b.range}
                  </span>
                </div>
              );
            })}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default AccountHealthBadge;
